import { format } from 'date-fns/format'
import _get from 'lodash/get'
import _uniqBy from 'lodash/uniqBy'
import _uniqWith from 'lodash/uniqWith'
import { P, match } from 'ts-pattern'
import * as z from 'zod'

import { HTTP_METHOD } from '@nxt-insurance/api-service'
import * as constants from '@nxt-insurance/constants'

import {
  BREED_IMAGE_COLLECTION,
  CONSENT_LEGAL_TEXT_KEYS,
  MAX_CROSS_BREEDS,
  UNKNOWN_BREED_OPTION,
} from '@flows/configs/petHealthV2/constants'
import { v2Text } from '@flows/configs/petHealthV2/steps/elements/shared'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import { zipCodeValidatorHookCallback } from '@flows/configs/shared/validatorHooks'
import type { StepConfigParams } from '@flows/configs/types'
import type { SearchableListOption } from '@flows/core/types/stepContentTypes'
import type {
  AnySimpleOnPrepareHook,
  AnySimpleOnSubmitHook,
  FlowValues,
} from '@flows/core/types/stepTypes'
import { BaseSchema_PetHealthV2GetsafeDe } from '@flows/insurance/schemas'

type PetHealthV2Params = z.infer<typeof BaseSchema_PetHealthV2GetsafeDe>
type BreedParams = {
  first_breed_name: PetHealthV2Params['first_breed_name']
  second_breed_name: NonNullable<PetHealthV2Params['second_breed_name']> | null
  breed_type: PetHealthV2Params['breed_type']
}
export type HealthStatusEntry = NonNullable<PetHealthV2Params['health_status_entries']>[number]
type BreedValues = Partial<
  Record<
    keyof Pick<PetHealthV2Params, 'species' | 'first_breed_name' | 'second_breed_name'>,
    unknown
  >
>

const SHOULDER_HEIGHT_SPECIES: PetHealthV2Params['species'] = 'dog'
const STEP_AFTER_BREEDS = { native: 'postal_code', web: 'pet_quote' } as const

const breedListSchema = z.array(z.string())

const isUnknownBreed = (breed: unknown) =>
  typeof breed === 'string' && breed.trim().toLowerCase() === UNKNOWN_BREED_OPTION
export const isMissingBreed = (breed: unknown) =>
  breed === undefined || breed === null || (typeof breed === 'string' && breed.trim() === '')
const compareBreeds = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' })

const toKnownBreeds = (breeds: readonly string[]) =>
  _uniqBy(
    breeds
      .map((breed) => breed.trim())
      .filter((breed) => !isMissingBreed(breed) && !isUnknownBreed(breed)),
    (breed) => breed.toLowerCase()
  )

const breedImageKey = (breed: string) => breed.trim().toLowerCase()

export const toBreedOptions = (
  breeds: readonly string[],
  unknownLabelKey: string,
  images: Readonly<Record<string, string>> = {}
): SearchableListOption[] => {
  const withImage = (breed: string) => {
    const image = images[breedImageKey(breed)]
    return image ? { image } : {}
  }
  return [
    ...toKnownBreeds(breeds)
      .sort(compareBreeds)
      .map(
        (breed): SearchableListOption => ({
          value: breed,
          label: { type: 'nonTranslatableText', text: breed },
          ...withImage(breed),
        })
      ),
    {
      value: UNKNOWN_BREED_OPTION,
      label: { type: 'translatableText', translationKey: unknownLabelKey },
    },
  ]
}

const libraryImagesSchema = z.array(
  z.object({ key: z.string(), url: z.string(), aliases: z.array(z.string()) })
)

const breedImagesSchema = z.object({
  cat: z.record(z.string(), z.string()),
  dog: z.record(z.string(), z.string()),
})
type BreedImages = z.infer<typeof breedImagesSchema>

const loadBreedImages = async (params: StepConfigParams): Promise<BreedImages | undefined> => {
  try {
    const response = await params.clientHelpers.apiService.call({
      serviceName: 'personalizationService',
      method: HTTP_METHOD.GET,
      url: `/api/v1/library_images/${BREED_IMAGE_COLLECTION}`,
    })
    const images = libraryImagesSchema.parse(response.data)
    const imagesOf = (species: PetHealthV2Params['species']) =>
      Object.fromEntries(
        images
          .filter((image) => image.key.startsWith(`${species}_`))
          .flatMap((image) =>
            image.aliases.map((alias) => [
              breedImageKey(alias),
              `${image.url}?w=480&h=480&fit=crop&auto=format`,
            ])
          )
      )
    return { cat: imagesOf('cat'), dog: imagesOf('dog') }
  } catch (error) {
    params.clientHelpers.reportError?.(error, {
      context: '[petHealthV2] breed images could not be loaded',
      productKey: params.productKey,
    })
    return undefined
  }
}

const getBreedImages = (params: StepConfigParams, flowValues: FlowValues) => {
  const loaded = breedImagesSchema.safeParse(
    _get(flowValues, params.helperFunctions.getTransientPath('breed_images'))
  )
  return loaded.success ? Promise.resolve(loaded.data) : loadBreedImages(params)
}

const loadedBreedListsSchema = z.object({
  cat: breedListSchema.min(1),
  dog: breedListSchema.min(1),
})
type BreedLists = z.infer<typeof loadedBreedListsSchema>

const EMPTY_BREED_LISTS: BreedLists = { cat: [], dog: [] }

const loadBreedLists = async (params: StepConfigParams): Promise<BreedLists | undefined> => {
  try {
    const response = await params.clientHelpers.apiService.call({
      serviceName: 'insurance',
      method: HTTP_METHOD.GET,
      url: `/api/v2/markets/${params.clientHelpers.getMarket()}/products/${
        params.productKey
      }/configuration_params_options`,
    })
    return loadedBreedListsSchema.parse(_get(response.data, ['first_breed_name', 0]))
  } catch (error) {
    params.clientHelpers.reportError?.(error, {
      context: '[petHealthV2] breed lists could not be loaded',
      productKey: params.productKey,
    })
    return undefined
  }
}

const getBreedLists = (params: StepConfigParams, flowValues: FlowValues) => {
  const loaded = loadedBreedListsSchema.safeParse(
    _get(flowValues, params.helperFunctions.getTransientPath('breeds_by_species'))
  )
  return loaded.success ? Promise.resolve(loaded.data) : loadBreedLists(params)
}

const seedCrossBreeds = (answered: readonly string[]) => [
  ...toKnownBreeds(answered),
  ...answered.filter(isUnknownBreed).map(() => UNKNOWN_BREED_OPTION),
]

const isOfferedBreed = (offered: readonly string[], breed: string) =>
  [...offered, UNKNOWN_BREED_OPTION].some((option) => compareBreeds(option, breed.trim()) === 0)

export const isBreedOfferedForSpecies = (
  params: StepConfigParams,
  flowValues: FlowValues,
  breed: string
) => {
  const { getParamPath, getTransientPath } = params.helperFunctions
  const lists = loadedBreedListsSchema.safeParse(
    _get(flowValues, getTransientPath('breeds_by_species'))
  )
  const species = BaseSchema_PetHealthV2GetsafeDe.shape.species.safeParse(
    _get(flowValues, getParamPath('species'))
  )
  return !lists.success || !species.success || isOfferedBreed(lists.data[species.data], breed)
}

const syncPickedBreeds = (
  params: StepConfigParams,
  flowValues: FlowValues,
  breeds: readonly string[]
) => {
  const { getParamPath, getTransientPath } = params.helperFunctions
  const isOffered = (breed: string) => isOfferedBreed(breeds, breed)
  const breedPaths = [getParamPath('first_breed_name'), getParamPath('second_breed_name')]
  const stalePaths = breedPaths.filter((path) => {
    const breed = _get(flowValues, path)
    return typeof breed === 'string' && !isMissingBreed(breed) && !isOffered(breed)
  })
  const picked = breedListSchema.catch([]).parse(_get(flowValues, getTransientPath('cross_breeds')))
  const answered = breedPaths
    .filter((path) => !stalePaths.includes(path))
    .map((path) => _get(flowValues, path))
    .filter((breed): breed is string => typeof breed === 'string')
  const crossBreeds = picked.length > 0 ? picked : seedCrossBreeds(answered)

  return {
    ...Object.fromEntries(stalePaths.map((path) => [path, null])),
    [getTransientPath('cross_breeds')]: crossBreeds.filter(isOffered),
  }
}

export const fetchBreedOptions = (params: StepConfigParams, { withImages = true } = {}) =>
  ({
    type: 'simpleHook',
    callback: async (flowValues: FlowValues) => {
      const { getParamPath, getTransientPath } = params.helperFunctions
      const species = BaseSchema_PetHealthV2GetsafeDe.shape.species.safeParse(
        _get(flowValues, getParamPath('species'))
      )
      const [lists, images] = await Promise.all([
        getBreedLists(params, flowValues),
        withImages && species.success ? getBreedImages(params, flowValues) : undefined,
      ])
      const offered = lists ?? EMPTY_BREED_LISTS
      const breeds = species.success ? offered[species.data] : undefined

      return {
        formValues: {
          [getTransientPath('breeds_by_species')]: offered,
          [getTransientPath('breed_options')]:
            breeds &&
            toBreedOptions(
              breeds,
              v2Text('steps.breeds.unknown').translationKey,
              species.success ? images?.[species.data] : undefined
            ),
          [getTransientPath('breeds_unavailable')]: lists === undefined,
          [getTransientPath('unknown_breed_image')]: species.success
            ? images?.[species.data][UNKNOWN_BREED_OPTION]
            : undefined,
          ...(images && { [getTransientPath('breed_images')]: images }),
          ...(lists && breeds && syncPickedBreeds(params, flowValues, breeds)),
        },
      }
    },
  }) as const satisfies AnySimpleOnPrepareHook

export type ConsentName = keyof typeof CONSENT_LEGAL_TEXT_KEYS
export const CONSENT_NAMES = Object.keys(CONSENT_LEGAL_TEXT_KEYS) as ConsentName[]

const legalTextSchema = z.object({
  heading: z.string(),
  contentVersion: z.object({ key: z.string(), contentHtml: z.string() }),
})

export const consentTextPath = (
  params: StepConfigParams,
  name: ConsentName,
  part: 'heading' | 'content_html' | 'version'
) => params.helperFunctions.getTransientPath(`consents.${name}.${part}`)

export const fetchConsentTexts = (params: StepConfigParams) =>
  ({
    type: 'simpleHook',
    callback: async () => {
      const response = await params.clientHelpers.apiService.call({
        serviceName: 'personalizationService',
        method: HTTP_METHOD.GET,
        url: `/api/v3/legal_texts/versioned?market=${params.clientHelpers.getMarket()}`,
      })

      const formValues = Object.fromEntries(
        CONSENT_NAMES.flatMap((name) => {
          const legalText = legalTextSchema.safeParse(
            _get(response.data, CONSENT_LEGAL_TEXT_KEYS[name])
          )
          if (!legalText.success) {
            throw new Error(`Assertion: legal text ${CONSENT_LEGAL_TEXT_KEYS[name]} is undefined`)
          }

          return [
            [consentTextPath(params, name, 'heading'), legalText.data.heading],
            [
              consentTextPath(params, name, 'content_html'),
              legalText.data.contentVersion.contentHtml,
            ],
            [consentTextPath(params, name, 'version'), legalText.data.contentVersion.key],
          ]
        })
      )

      return { formValues }
    },
  }) as const satisfies AnySimpleOnPrepareHook

const isUnknownOrMissingBreed = (breed: unknown) => isMissingBreed(breed) || isUnknownBreed(breed)

export const areAllBreedsUnknown = ({ first_breed_name, second_breed_name }: BreedValues) =>
  isUnknownOrMissingBreed(first_breed_name) && isUnknownOrMissingBreed(second_breed_name)

export const requiresShoulderHeight = (values: BreedValues) =>
  values.species === SHOULDER_HEIGHT_SPECIES && areAllBreedsUnknown(values)

export const resolveStepAfterBreeds =
  (platform: 'web' | 'native', params: StepConfigParams) =>
  (flowValues: FlowValues): 'shoulder_height' | 'postal_code' | 'pet_quote' => {
    const configurationParameters: BreedValues =
      params.helperFunctions.getProductConfigurationParameters(flowValues) ?? {}
    return requiresShoulderHeight(configurationParameters)
      ? 'shoulder_height'
      : STEP_AFTER_BREEDS[platform]
  }

export const nextStepAfterBreeds = (platform: 'web' | 'native', params: StepConfigParams) => ({
  valueFunction: resolveStepAfterBreeds(platform, params),
  valueToStepName: {
    shoulder_height: 'shoulder_height',
    [STEP_AFTER_BREEDS[platform]]: STEP_AFTER_BREEDS[platform],
  },
})

export const mapBreedsToParams = (breeds: readonly string[]): BreedParams => {
  const knownBreeds = toKnownBreeds(breeds)
  const unknownBreed =
    knownBreeds.length === 0 || breeds.some(isUnknownOrMissingBreed) ? [UNKNOWN_BREED_OPTION] : []
  const [first = UNKNOWN_BREED_OPTION, second = null] = [...knownBreeds, ...unknownBreed].slice(
    0,
    MAX_CROSS_BREEDS
  )

  return {
    first_breed_name: first,
    second_breed_name: second,
    breed_type: second === null ? 'pedigree' : 'crossbreed',
  }
}

const breedOptionImagesSchema = z
  .array(z.object({ value: z.unknown(), image: z.string().optional() }))
  .catch([])

const petPortrait = (
  params: StepConfigParams,
  flowValues: FlowValues,
  breed: string | null | undefined
) => {
  const { getTransientPath } = params.helperFunctions
  if (breed === UNKNOWN_BREED_OPTION) {
    return _get(flowValues, getTransientPath('unknown_breed_image')) ?? null
  }
  const options = breedOptionImagesSchema.parse(_get(flowValues, getTransientPath('breed_options')))
  return options.find((option) => option.value === breed)?.image ?? null
}

export const submitBreeds = (
  params: StepConfigParams,
  breedType: PetHealthV2Params['breed_type'] | 'derive',
  paths: readonly string[]
) =>
  ({
    type: 'simpleHook',
    callback: (flowValues: FlowValues) => {
      const { getParamPath } = params.helperFunctions
      const picked = paths
        .flatMap((path) => [_get(flowValues, path)].flat())
        .filter((breed): breed is string => typeof breed === 'string')
        .filter((breed) => isBreedOfferedForSpecies(params, flowValues, breed))
      const mapped = mapBreedsToParams(picked.filter((breed) => !isMissingBreed(breed)))
      const resolvedType = breedType === 'derive' ? mapped.breed_type : breedType
      const secondBreed =
        resolvedType === 'crossbreed' ? mapped.second_breed_name ?? UNKNOWN_BREED_OPTION : null

      return {
        formValues: {
          [getParamPath('first_breed_name')]: mapped.first_breed_name,
          [getParamPath('second_breed_name')]: secondBreed,
          [getParamPath('breed_type')]: resolvedType,
          [params.helperFunctions.getTransientPath('pet_portrait')]:
            resolvedType === 'pedigree'
              ? petPortrait(params, flowValues, mapped.first_breed_name)
              : null,
          ...copyCustomerZipCode(params, flowValues).formValues,
        },
      }
    },
  }) as const satisfies AnySimpleOnSubmitHook

const zipCodeSchema = BaseSchema_PetHealthV2GetsafeDe.shape.zip_code

export const hasCustomerZipCode = (flowValues: FlowValues): boolean =>
  zipCodeSchema.safeParse(flowValues.zip_code).success

export const postalCodeErrorMap: z.ZodErrorMap = (issue, ctx) =>
  schemaUtils.zipCodeErrorMap(issue) ?? { message: ctx.defaultError }

export const validateZipCode = (params: StepConfigParams) => (flowValues: FlowValues) =>
  zipCodeValidatorHookCallback(params)({
    ...flowValues,
    zip_code: _get(flowValues, params.helperFunctions.getParamPath('zip_code')),
  })

export const copyCustomerZipCode = (params: StepConfigParams, flowValues: FlowValues) => {
  const paramPath = params.helperFunctions.getParamPath('zip_code')
  const isAnswered = zipCodeSchema.safeParse(_get(flowValues, paramPath)).success

  return {
    formValues:
      hasCustomerZipCode(flowValues) && !isAnswered ? { [paramPath]: flowValues.zip_code } : {},
  }
}

const isSameCondition = (a: HealthStatusEntry, b: HealthStatusEntry) =>
  a.health_status_area === b.health_status_area &&
  a.health_status_diagnosis === b.health_status_diagnosis

export const addCondition = (
  conditions: readonly HealthStatusEntry[],
  added: HealthStatusEntry
): HealthStatusEntry[] => _uniqWith([...conditions, added], isSameCondition)

export const replaceCondition = (
  conditions: readonly HealthStatusEntry[],
  { previous, next }: { previous: HealthStatusEntry; next: HealthStatusEntry }
): HealthStatusEntry[] =>
  _uniqWith(
    conditions.map((existing) => (isSameCondition(existing, previous) ? next : existing)),
    isSameCondition
  )

const conditionListSchema = BaseSchema_PetHealthV2GetsafeDe.shape.health_status_entries.unwrap()
const conditionIndexSchema = z.number().int().nonnegative()
const conditionActionSchema = z.union([conditionIndexSchema, z.literal('new')])

const readConditions = (params: StepConfigParams, flowValues: FlowValues) =>
  conditionListSchema
    .catch([])
    .parse(_get(flowValues, params.helperFunctions.getParamPath('health_status_entries')))

const readConditionAction = (params: StepConfigParams, flowValues: FlowValues) =>
  conditionActionSchema
    .optional()
    .catch(undefined)
    .parse(_get(flowValues, params.helperFunctions.getTransientPath('condition_action')))

const conditionAt = (params: StepConfigParams, flowValues: FlowValues, index: number | null) =>
  index === null ? undefined : readConditions(params, flowValues)[index]

const editedCondition = (params: StepConfigParams, flowValues: FlowValues) =>
  conditionAt(
    params,
    flowValues,
    conditionIndexSchema
      .nullable()
      .catch(null)
      .parse(_get(flowValues, params.helperFunctions.getTransientPath('condition_edit_index')))
  )

export const startConditionAction = (params: StepConfigParams, flowValues: FlowValues) => {
  const { getTransientPath } = params.helperFunctions
  const action = readConditionAction(params, flowValues)
  if (action === undefined) {
    return {}
  }
  const index = action === 'new' ? null : action

  return {
    [getTransientPath('condition')]: conditionAt(params, flowValues, index) ?? null,
    [getTransientPath('condition_edit_index')]: index,
    [getTransientPath('condition_action')]: null,
  }
}

export const resolveStepAfterConditionsQuestion =
  (params: StepConfigParams) => (flowValues: FlowValues) =>
    match({
      hasConditions: _get(
        flowValues,
        params.helperFunctions.getTransientPath('has_pre_existing_conditions')
      ),
      action: readConditionAction(params, flowValues),
      count: readConditions(params, flowValues).length,
    })
      .returnType<'condition_area' | 'current_health'>()
      .with({ hasConditions: true, action: P.not(undefined) }, () => 'condition_area')
      .with({ hasConditions: true, count: 0 }, () => 'condition_area')
      .otherwise(() => 'current_health')

export const saveCondition = (params: StepConfigParams, flowValues: FlowValues) => {
  const { getParamPath, getTransientPath } = params.helperFunctions
  const condition = conditionListSchema.element.parse(
    _get(flowValues, getTransientPath('condition'))
  )
  const conditions = readConditions(params, flowValues)
  const previous = editedCondition(params, flowValues)

  return {
    formValues: {
      [getParamPath('health_status_entries')]: previous
        ? replaceCondition(conditions, { previous, next: condition })
        : addCondition(conditions, condition),
      [getTransientPath('condition_edit_index')]: null,
      [getTransientPath('condition')]: null,
    },
  }
}

// Complaints without a listed condition are reported as an unknown area (broad pre-existing condition clause)
export const UNKNOWN_AREA_ENTRY = {
  health_status_area: 'unknown',
} as const satisfies HealthStatusEntry

const isUnknownAreaEntry = (entry: HealthStatusEntry) =>
  entry.health_status_area === UNKNOWN_AREA_ENTRY.health_status_area

export const listedConditions = (params: StepConfigParams, flowValues: FlowValues) =>
  readConditions(params, flowValues).filter((entry) => !isUnknownAreaEntry(entry))

export const submitHealthStatus = (params: StepConfigParams, flowValues: FlowValues) => {
  const { getParamPath } = params.helperFunctions
  const hasComplaints = _get(flowValues, getParamPath('current_health_status')) !== 'healthy'
  const conditions = listedConditions(params, flowValues)

  return {
    formValues: {
      [getParamPath('health_status_entries')]:
        hasComplaints && conditions.length === 0 ? [UNKNOWN_AREA_ENTRY] : conditions,
      [getParamPath('health_status_acknowledged_on')]: hasComplaints
        ? format(new Date(), constants.SERVER_DATE_FORMAT)
        : null,
    },
  }
}
